import React, { useState } from 'react';
import { IonButton, IonIcon, useIonRouter } from '@ionic/react';
import { checkmarkOutline, closeOutline, calendarOutline } from 'ionicons/icons';
import { useTranslation } from 'react-i18next';
import { GolfAvatar } from '../ui/GolfAvatar';
import { formatDate } from '@/utils/dateUtils';
import { GameService } from '@/services/GameService';
import { mediumImpact } from '@/utils/capacitorUtils';
import { supabase } from '@/utils/supabase';

interface GameInvitationNotificationProps {
  gameId: string;
  gameName: string;
  gameDate: string;
  clubName: string;
  courseName: string;
  senderInfo: {
    name: string;
    username: string;
    avatar: string;
  };
  notificationId: string;
  userId: string;
  data: Record<string, any>;
}

export const GameInvitationNotification: React.FC<GameInvitationNotificationProps> = ({
  gameId,
  gameName,
  gameDate,
  clubName,
  courseName,
  senderInfo,
  notificationId,
  userId,
  data
}) => {
  const { t } = useTranslation();
  const router = useIonRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [responded, setResponded] = useState(false);
  const [response, setResponse] = useState<'accepted' | 'declined' | null>(null);

  const handleAccept = async () => {
    try {
      setIsLoading(true);
      await mediumImpact();
      
      // Update player status in the database
      await GameService.updatePlayerStatus(gameId, 'accepted');
      
      // Update notification status in Supabase
      await supabase
        .from('notifications')
        .update({ 
          data: { 
            ...data, 
            status: 'accepted' 
          },
          updated_at: new Date()
        })
        .eq('id', notificationId)
        .eq('user_id', userId);
      
      setResponse('accepted');
      setResponded(true);
      setIsLoading(false);
    } catch (error) {
      console.error('Error accepting game invitation:', error);
      setIsLoading(false);
    }
  };

  const handleDecline = async () => {
    try {
      setIsLoading(true);
      await mediumImpact();
      
      // Update player status in the database
      await GameService.updatePlayerStatus(gameId, 'declined');
      
      // Update notification status in Supabase
      await supabase
        .from('notifications')
        .update({ 
          data: { 
            ...data, 
            status: 'declined' 
          },
          updated_at: new Date()
        })
        .eq('id', notificationId)
        .eq('user_id', userId);
      
      setResponse('declined');
      setResponded(true);
      setIsLoading(false);
    } catch (error) {
      console.error('Error declining game invitation:', error);
      setIsLoading(false);
    }
  };

  const navigateToGame = () => {
    router.push(`/game/${gameId}`);
  };

  return (
    <div className="flex w-full flex-col p-3">
      <div className="flex items-center space-x-3">
        <GolfAvatar
          name={senderInfo.name}
          imageUrl={senderInfo.avatar}
          className="size-10 rounded-full"
        />
        <div className="flex-1">
          <p className="text-sm">
            <span className="font-semibold">{senderInfo.name}</span>{' '}
            {t('invited_you_to_game')}
          </p>
          <div className="mt-1 flex items-center text-xs text-gray-500">
            <IonIcon icon={calendarOutline} className="mr-1" />
            <span>{formatDate(new Date(gameDate))}</span>
          </div>
          <p className="mt-1 text-xs font-medium">
            {gameName} • {clubName} {courseName ? `(${courseName})` : ''}
          </p>
        </div>
      </div>

      {!responded ? (
        <div className="mt-3 flex justify-end space-x-2">
          <IonButton
            fill="outline"
            size="small"
            color="medium"
            disabled={isLoading}
            onClick={handleDecline}
            className="rounded-full"
          >
            <IonIcon slot="start" icon={closeOutline} />
            {t('decline')}
          </IonButton>
          <IonButton
            fill="solid"
            size="small"
            color="primary"
            disabled={isLoading}
            onClick={handleAccept}
            className="rounded-full"
          >
            <IonIcon slot="start" icon={checkmarkOutline} />
            {t('accept')}
          </IonButton>
        </div>
      ) : (
        <div className="mt-3 flex justify-end">
          <IonButton
            fill="clear"
            size="small"
            onClick={navigateToGame}
            className="rounded-full"
          >
            {response === 'accepted' ? t('view_game') : t('view_details')}
          </IonButton>
        </div>
      )}
    </div>
  );
};
